import {
  // AustraliaMapIcon,
  CloseIcon,
  GroupRadio,
  HomeFillIcon,
  IncDec, // OverseasIcon,
  RelationshipIcon,
  SuperSplitIcon
} from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { GetStarted_AssetSchema } from "definitions";
import { GetStartedStoreTypes } from "interfaces";
import { FC } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface IProps {
  formId: string;
  store: GetStartedStoreTypes;
  onSubmit: SubmitHandler<GetStartedStoreTypes>;
}

const AssetInformation: FC<IProps> = ({ formId, store, onSubmit }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: store,
    resolver: yupResolver(GetStarted_AssetSchema)
  });

  const groupQuestions = [
    {
      title: "Do either of you have any real estate?",
      subText: "Select “yes” even if you’re not dividing it.",

      id: "real_estate",
      name: "finance.real_estate",
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "no") {
          setValue("finance.real_estate_num", 0);
        }
      }
    },
    {
      title: "Do you need to do a super split?",
      subText: `A super split means transferring super from one person’s super fund to the other spouse’s super fund. `,
      id: "super_split",
      name: "finance.super_split",
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "no") {
          setValue("finance.super_split_num", 0);
        }
      }
    },
    {
      title: "Do either of you have assets overseas?",
      id: "overseas_assets",
      name: "finance.overseas_assets"
    },
    {
      title: "Do either of you have a business?",
      id: "business",
      name: "finance.business",
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "no") {
          setValue("finance.business_trust", "");
          setValue("finance.business_nt_num", 0);
          setValue("finance.business_trust_num", 0);
        }
      }
    },
    {
      title: "Is there a business associated with a trust",
      id: "business_trust",
      name: "finance.business_trust",
      dependent: "finance.business",
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "no") {
          setValue("finance.business_trust_num", 0);
        }
      }
    },
    {
      title: (
        <span>
          Do either of you have an interest in a trust
          {watch("finance.business_trust") === "yes" ? ` (other than a business trust)` : ""}?
        </span>
      ),
      subText:
        "Eg a family trust or self managed super fund, whether beneficiary, trustee or another role. Even if you're not dividing it. ",
      id: "other_trust",
      name: "finance.other_trust",
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
        if (ev.target.value === "no") {
          setValue("finance.trust_num", 0);
        }
      }
    },
    {
      title: "Have you lodged consent order documents with the court to divide your assets and debts?",
      subText: "Select yes if you have lodged your court documents or if the court has issued a requisition",
      id: "property_consent_lodged",
      name: "finance.property_consent_lodged"
    },
    {
      title: "Do you and your partner have combined net assets over $1.5 million?",
      subText: "Net assets means assets minus debts.",
      id: "joint_assets_1_5m_exceed",
      name: "finance.joint_assets_1_5m_exceed"
    }
  ];

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="mb-8 pb-8 border-b space-y-2">
        <RelationshipIcon classes="w-14 h-14 mx-auto" />
        <h3 className="text-center">Asset Information</h3>
      </div>

      <GroupRadio
        register={register}
        watch={watch}
        questions={groupQuestions}
        errors={errors}
        titleClasses="text-base font-semibold"
      />
      {watch("finance.real_estate") === "no" ? (
        <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
          <div className="w-8">
            <CloseIcon classes="w-7 text-red-500" />
          </div>
          <div className="w-[calc(100%_-_2rem] pl-5">
            <p className="font-bold">No Real Estate </p>
            <p className="text-sm text-gray-500 font-light">You have stated neither of you have any real estate.</p>
          </div>
        </div>
      ) : watch("finance.real_estate") === "yes" ? (
        <IncDec
          register={register}
          watch={watch}
          name="finance.real_estate_num"
          id="finance.real_estate_num"
          title="How many properties do you both own?"
          subTitle="Include all real estate owned by you and your partner, even if it’s not being divided."
          setValue={setValue}
          error={errors?.finance?.real_estate_num?.message ?? ""}
          icon={HomeFillIcon}
        />
      ) : null}

      {/* Super Split */}
      {watch("finance.super_split") === "no" ? (
        <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
          <div className="w-8">
            <CloseIcon classes="w-7 text-red-500" />
          </div>
          <div className="w-[calc(100%_-_2rem] pl-5">
            <p className="font-bold">No Super Split Required </p>
            <p className="text-sm text-gray-500 font-light">
              You do not intend to transfer any superannuation between your super funds.
            </p>
          </div>
        </div>
      ) : watch("finance.super_split") === "yes" ? (
        <IncDec
          register={register}
          watch={watch}
          name="finance.super_split_num"
          id="finance.super_split_num"
          title="How many super splits are needed?"
          subTitle="You only need a super split if you’re transferring super from one person’s super account to the person’s super account."
          setValue={setValue}
          error={errors?.finance?.super_split_num?.message}
          icon={SuperSplitIcon}
        />
      ) : null}

      {/* Overseas Assets  */}
      {/* {watch("finance.overseas_assets") === "no" ? (
        <li className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
          <div className="w-8">
            <AustraliaMapIcon />
          </div>
          <div className="w-[calc(100%_-_2rem] pl-5">
            <p className="font-bold">All your assets and debts are within Australia</p>
            <p className="text-sm text-gray-500 font-light">
              You and your partner do not have any assets or debts overseas.
            </p>
          </div>
        </li>
      ) : (
        <div className="flex items-center bg-[#f2f2f2] p-3 rounded-[10px]">
          <div className="w-8">
            <OverseasIcon strokeColor="w-8 h-auto" />
          </div>
          <div className="w-[calc(100%_-_2rem] pl-5">
            <p className="font-bold">Overseas assets (extra charge)</p>
            <p className="text-sm text-gray-500 font-light">
              You mentioned that you have assets overseas. This is an additional fee of $440. We cannot give legal
              advice on overseas assets, but we can list them in the consent orders.
            </p>
          </div>
        </div>
      )} */}

      {/* Business No Trust  */}
      {watch("finance.business") === "yes" ? (
        <IncDec
          register={register}
          watch={watch}
          name="finance.business_nt_num"
          id="finance.business_nt_num"
          title="How many businesses (without a trust) do you both own?"
          subTitle="Include it, even if you’re not dividing it."
          setValue={setValue}
          error={errors?.finance?.business_nt_num?.message}
        />
      ) : null}

      {watch("finance.business_trust") === "yes" ? (
        <IncDec
          register={register}
          watch={watch}
          name="finance.business_trust_num"
          id="finance.business_trust_num"
          title="How many businesses (linked to a trust) are there?"
          subTitle="Eg ABC Pty Ltd as trustee for the Smith Family Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
          setValue={setValue}
          error={errors?.finance?.business_trust_num?.message}
        />
      ) : null}

      {watch("finance.other_trust") === "yes" ? (
        <IncDec
          register={register}
          watch={watch}
          name="finance.trust_num"
          id="finance.trust_num"
          title="How many (non-business) trusts do you both have an interest in?"
          subTitle="Eg: Family Trust and Unit Trust. Include every trust either of you have an interest in, even if you’re not dividing it."
          setValue={setValue}
          error={errors?.finance?.trust_num?.message}
        />
      ) : null}
    </form>
  );
};

export default AssetInformation;
